import { FC, useState } from 'react';
import {
  DataRoomAccessPermission,
  DataRoomShareEffect,
  MAX_SHARE_RECIPIENTS_PER_REQUEST,
  shareNeedsWarning,
} from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  DoubleChevronIcon,
  EditIcon,
  InfoCircle,
  XCloseIcon,
} from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import { Tag, TagVariant } from '../../../../../components/tag/index';
import { ShareCandidate, useDataRoom, useShareDataRoom } from '../../../../../hooks/space/index';
import { StringKey } from '../../../../../lang/index';
import { AccessAvatar } from './access-avatar';
import { InviteExternalTab } from './invite-external-tab';
import { InviteInternalTab } from './invite-internal-tab';
import { InvitePermissionToggle } from './invite-permission-toggle';

export enum InviteStep {
  CONFIRM = 'confirm',
  PICK = 'pick',
}

export enum InviteTab {
  EXTERNAL = 'external',
  INTERNAL = 'internal',
}

export type SelectedInternal = {
  candidate: ShareCandidate;
  permission: DataRoomAccessPermission;
};

export type ExternalRecipient = {
  email: string;
  permission: DataRoomAccessPermission;
};

const TAB_LABEL: Record<InviteTab, StringKey> = {
  [InviteTab.EXTERNAL]: 'EXTERNAL_RECIPIENTS',
  [InviteTab.INTERNAL]: 'INTERNAL_STAKEHOLDERS',
};

/**
 * What the warning says about each name under it. `GRANT_DIRECTLY` has no entry: that branch is
 * what `shareNeedsWarning` filters out, so nobody on this list carries it.
 */
const SHARE_EFFECT_STATE: Partial<Record<DataRoomShareEffect, StringKey>> = {
  [DataRoomShareEffect.AUTO_INVITE]: 'SHARE_EFFECT_INACTIVE',
  [DataRoomShareEffect.RESTORE_ACCESS]: 'SHARE_EFFECT_ACCESS_REVOKED',
};

/**
 * Opened by Manage access and rendered from inside it, so that list stays on screen behind this
 * one — the design draws the two stacked, not swapped. It takes the room and the way out as props
 * rather than reading the modal store, which holds one modal at a time.
 */
export const InvitePeopleModal: FC<{ dataRoomId: string; onClose: () => void }> = ({
  dataRoomId,
  onClose,
}) => {
  const [step, setStep] = useState(InviteStep.PICK);
  const [tab, setTab] = useState(InviteTab.INTERNAL);
  const [isRestrictedOpen, setIsRestrictedOpen] = useState(false);
  const [internal, setInternal] = useState<SelectedInternal[]>([]);
  const [external, setExternal] = useState<ExternalRecipient[]>([]);

  const { dataRoom } = useDataRoom(dataRoomId);
  const { isPending, shareDataRoom } = useShareDataRoom();

  const totalSelected = internal.length + external.length;
  const isOverLimit = totalSelected > MAX_SHARE_RECIPIENTS_PER_REQUEST;
  const restricted = internal.filter(({ candidate }) => shareNeedsWarning(candidate.shareEffect));

  const takenEmails = new Set([
    ...external.map(({ email }) => email.toLowerCase()),
    ...internal.map(({ candidate }) => candidate.email.toLowerCase()),
  ]);

  const confirm = () => {
    if (!totalSelected || isOverLimit || isPending) return;

    shareDataRoom(
      {
        id: dataRoomId,
        recipients: [
          ...internal.map(({ candidate, permission }) => ({
            email: candidate.email,
            permission,
          })),
          ...external.map(({ email, permission }) => ({ email, permission })),
        ],
      },
      { onSuccess: onClose },
    );
  };

  const removeInternal = (id: string) =>
    setInternal((current) => current.filter(({ candidate }) => candidate.id !== id));

  const removeExternal = (email: string) =>
    setExternal((current) => current.filter((recipient) => recipient.email !== email));

  const setInternalPermission = (id: string, permission: DataRoomAccessPermission) =>
    setInternal((current) =>
      current.map((entry) => (entry.candidate.id === id ? { ...entry, permission } : entry)),
    );

  const setExternalPermission = (email: string, permission: DataRoomAccessPermission) =>
    setExternal((current) =>
      current.map((entry) => (entry.email === email ? { ...entry, permission } : entry)),
    );

  return (
    <ModalWrapper
      // A drawer off the right edge, the way the Add SAFE and dividend flows open, rather than a
      // second dialog in the middle.
      //
      // Both sheets sit at z-51 and every overlay at z-50, so this one's dim would slide under the
      // access list that opened it and leave that list as bright as before. Lifted over it — 52
      // for the dim, 53 for the drawer — the list reads as the layer underneath. The selects in
      // here portal at z-60 and still clear both.
      className="z-[53] w-full max-w-[731px] border-transparent bg-transparent py-2 pl-0 pr-2 shadow-none"
      control={{ onOpenChange: (open) => !open && onClose(), open: true }}
      onBgClick={onClose}
      overlayClassName="z-[52]"
      side="RIGHT"
    >
      <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-l border-gray-300 bg-gray-100 shadow-[4px_0px_24px_0px_rgba(52,64,84,0.04),4px_0px_40px_-2px_rgba(8,17,32,0.1),-2px_0px_4px_-2px_rgba(8,17,32,0.06)]">
        {/* 72 tall, the title block inset 24 from the left and 12 from the top, the cross 24 in
            from the right — the blade's own header, not the centred dialog's. */}
        <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-gray-100 bg-white px-6 py-3">
          <div className="flex flex-col">
            <p className="text-xl font-semibold leading-8 text-gray-700">
              <AppFormattedMessage id="INVITE_PEOPLE_TITLE" />
            </p>
            <p className="text-xs font-[450] leading-4 text-gray-500">
              <AppFormattedMessage id="DATA_ROOM" /> - {dataRoom?.name ?? ''}
            </p>
          </div>
          <button aria-label="close" onClick={onClose} type="button">
            <XCloseIcon className="size-[21px]" iconColor="#344054" />
          </button>
        </div>

        <div
          className={twMerge(
            'flex min-h-0 flex-1 flex-col bg-white pb-2 pt-4',
            restricted.length > 0 && 'border-b border-sunbeam-100',
          )}
        >
          {/* The rule runs the full inset width; the tabs start a further 16 in, 16 apart, each 20
              of text over 16 of air and a 2px marker — 38 in all. */}
          {step === InviteStep.PICK && (
            <div className="shrink-0 px-4">
              <div className="flex gap-4 border-b border-gray-200 pl-4">
                {[InviteTab.INTERNAL, InviteTab.EXTERNAL].map((current) => {
                  const count = current === InviteTab.INTERNAL ? internal.length : external.length;

                  return (
                    <button
                      className={twMerge(
                        'flex h-[38px] items-center gap-1 border-b-2 px-1 pb-4 text-sm leading-5',
                        tab === current
                          ? 'border-brand-700 font-[550] text-brand-700'
                          : 'border-transparent font-[450] text-gray-400',
                      )}
                      key={current}
                      onClick={() => setTab(current)}
                      type="button"
                    >
                      <AppFormattedMessage id={TAB_LABEL[current]} />
                      {count > 0 && (
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-fireside-500 px-[4.8px] text-[9px] font-semibold leading-[11.2px] text-white">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div
            className={twMerge(
              'flex min-h-0 flex-1 flex-col overflow-y-auto px-4',
              step === InviteStep.PICK && 'pt-6',
            )}
          >
            {step === InviteStep.PICK && tab === InviteTab.INTERNAL && (
              <InviteInternalTab
                dataRoomId={dataRoomId}
                onPermissionChange={setInternalPermission}
                onToggle={(candidate, isSelected) =>
                  setInternal((current) =>
                    isSelected
                      ? [...current, { candidate, permission: DataRoomAccessPermission.VIEWER }]
                      : current.filter((entry) => entry.candidate.id !== candidate.id),
                  )
                }
                selected={internal}
              />
            )}

            {step === InviteStep.PICK && tab === InviteTab.EXTERNAL && (
              <InviteExternalTab
                dataRoomId={dataRoomId}
                onAdd={(recipient) => setExternal((current) => [...current, recipient])}
                onPermissionChange={setExternalPermission}
                onRemove={removeExternal}
                recipients={external}
                takenEmails={takenEmails}
              />
            )}

            {/* Taking the last recipient off the summary leaves it with nothing to summarise,
                and an empty sheet does not say why Confirm & Share is greyed out. */}
            {step === InviteStep.CONFIRM && totalSelected === 0 && (
              <div className="flex flex-1 flex-col items-center justify-center gap-2">
                <p className="text-sm font-[550] leading-5 text-gray-700">
                  <AppFormattedMessage id="NO_RECIPIENTS_SELECTED" />
                </p>
                <p className="text-xs font-[450] leading-4 text-gray-500">
                  <AppFormattedMessage id="GO_BACK_TO_ADD_RECIPIENTS" />
                </p>
              </div>
            )}

            {step === InviteStep.CONFIRM && (
              <div className="flex flex-col gap-6">
                {internal.length > 0 && (
                  <ConfirmSection
                    onEdit={() => {
                      setStep(InviteStep.PICK);
                      setTab(InviteTab.INTERNAL);
                    }}
                    title="INTERNAL_STAKEHOLDERS"
                  >
                    {internal.map(({ candidate, permission }) => (
                      <ConfirmRow
                        email={candidate.email}
                        isActive={candidate.isActive}
                        key={candidate.id}
                        name={candidate.name}
                        onPermissionChange={(next) => setInternalPermission(candidate.id, next)}
                        onRemove={() => removeInternal(candidate.id)}
                        permission={permission}
                      />
                    ))}
                  </ConfirmSection>
                )}

                {external.length > 0 && (
                  <ConfirmSection
                    onEdit={() => {
                      setStep(InviteStep.PICK);
                      setTab(InviteTab.EXTERNAL);
                    }}
                    title="EXTERNAL"
                  >
                    {external.map(({ email, permission }) => (
                      <ConfirmRow
                        email={email}
                        isExternal
                        key={email}
                        name={null}
                        onPermissionChange={(next) => setExternalPermission(email, next)}
                        onRemove={() => removeExternal(email)}
                        permission={permission}
                      />
                    ))}
                  </ConfirmSection>
                )}
              </div>
            )}
          </div>
        </div>

        {/* The bar above the footer: one line closed, and opened it says what the warning means
            and for whom. Its shadow is cast upwards, over the list, which is what separates it
            from the rows rather than a border on that side. */}
        {restricted.length > 0 && (
          <button
            className="flex w-full shrink-0 items-start gap-1 rounded-b-xl border-b border-gray-300 bg-sunbeam-50 px-6 py-3 text-left [filter:drop-shadow(0px_-12px_8px_rgba(16,24,40,0.08))_drop-shadow(0px_-4px_3px_rgba(16,24,40,0.03))]"
            onClick={() => setIsRestrictedOpen((current) => !current)}
            type="button"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <InfoCircle className="size-5 shrink-0" iconColor="#DC6803" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <span className="text-sm font-[550] leading-5 text-gray-800">
                  <AppFormattedMessage id="SOME_USERS_HAVE_RESTRICTED_ACCESS" />
                </span>
                {isRestrictedOpen && (
                  <>
                    <span className="text-xs font-[450] leading-4 text-gray-700">
                      <AppFormattedMessage id="SHARE_EFFECT_EXPLANATION" />
                    </span>
                    {/* One line each, and each says which of the two it is: the sentence above
                        covers both, so a bare list of names leaves the Admin to guess what is
                        about to happen to whom. */}
                    <span className="flex flex-col gap-1 text-xs font-[550] leading-4 text-sunbeam-700">
                      {restricted.map(({ candidate }) => {
                        const state = SHARE_EFFECT_STATE[candidate.shareEffect];

                        return (
                          <span key={candidate.id}>
                            {candidate.name}
                            {state && (
                              <>
                                {' ('}
                                <AppFormattedMessage id={state} />
                                {')'}
                              </>
                            )}
                          </span>
                        );
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>
            <span className="flex size-4 shrink-0 items-center justify-center">
              <DoubleChevronIcon
                className={twMerge(
                  'transition-transform',
                  isRestrictedOpen ? '-rotate-90' : 'rotate-90',
                )}
                iconColor="#344054"
              />
            </span>
          </button>
        )}

        {isOverLimit && (
          <div className="border-t border-fireside-200 bg-fireside-50 px-6 py-3">
            <p className="text-sm font-[450] text-fireside-600">
              <AppFormattedMessage
                id="MAX_RECIPIENTS_REACHED"
                values={{ count: MAX_SHARE_RECIPIENTS_PER_REQUEST }}
              />
            </p>
          </div>
        )}

        <div className="flex shrink-0 items-center justify-between px-6 py-4">
          <Button
            className="h-9 w-fit border border-gray-100 bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
            onClick={onClose}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <div className="flex items-center gap-3">
            {step === InviteStep.CONFIRM && (
              <Button
                className="h-9 w-fit border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
                onClick={() => setStep(InviteStep.PICK)}
                styleType="DEFAULT_ROUNDED"
              >
                <AppFormattedMessage id="BACK" />
              </Button>
            )}
            {step === InviteStep.PICK ? (
              <Button
                className="h-9 w-fit px-4 py-2.5"
                disabled={!totalSelected || isOverLimit}
                onClick={() => setStep(InviteStep.CONFIRM)}
              >
                <AppFormattedMessage id="NEXT" />
              </Button>
            ) : (
              <Button
                className="h-9 w-fit px-4 py-2.5"
                disabled={!totalSelected || isOverLimit || isPending}
                isLoading={isPending}
                onClick={confirm}
              >
                <AppFormattedMessage id="CONFIRM_AND_SHARE" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

const ConfirmSection: FC<{
  children: React.ReactNode;
  onEdit: () => void;
  title: StringKey;
}> = ({ children, onEdit, title }) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-3 rounded-t-lg bg-gray-50 px-4 py-2">
      <span className="min-w-0 flex-1 text-sm font-[550] leading-5 text-gray-700">
        <AppFormattedMessage id={title} />
      </span>
      <button aria-label="edit" onClick={onEdit} type="button">
        <EditIcon className="size-5" iconColor="#2565C8" />
      </button>
    </div>
    <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 px-4 pb-3 pt-4">
      {children}
    </div>
  </div>
);

const ConfirmRow: FC<{
  email: string;
  isActive?: boolean;
  isExternal?: boolean;
  name: null | string;
  onPermissionChange: (permission: DataRoomAccessPermission) => void;
  onRemove: () => void;
  permission: DataRoomAccessPermission;
}> = ({ email, isActive, isExternal, name, onPermissionChange, onRemove, permission }) => (
  <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
    <div className="flex min-w-0 items-center gap-3">
      <AccessAvatar label={name ?? email} labelClassName="text-[12.8px] leading-[19.2px]" />
      <div className="flex min-w-0 flex-col">
        <p className="truncate text-sm font-[450] leading-5 text-gray-700">{name ?? email}</p>
        {name && (
          <p className="truncate text-label-sm font-medium leading-4 text-gray-400">{email}</p>
        )}
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-4">
      {isExternal ? (
        <Tag
          borderColor="border-transparent"
          className="bg-[#FCFAFF] text-[#7F56D9]"
          title={<AppFormattedMessage id="EXTERNAL" />}
          variant={TagVariant.WHITE}
        />
      ) : (
        <Tag
          title={<AppFormattedMessage id={isActive ? 'ACTIVE' : 'INACTIVE'} />}
          variant={isActive ? TagVariant.GREEN : TagVariant.YELLOW}
        />
      )}
      <InvitePermissionToggle onChange={onPermissionChange} value={permission} />
      <button aria-label="remove" onClick={onRemove} type="button">
        <XCloseIcon className="size-4" iconColor="#344054" />
      </button>
    </div>
  </div>
);
